import { useMemo, useCallback } from 'react';
import { FormattedMessage, FormattedNumber, IntlShape, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';

import { IEarning, PeriodNameEnum, TPeriodName } from '@/modules/earning/model';
import { getCity } from '@/modules/partner/store/selectors';
import { formatDate } from '@/shared/utils/formatDate';
import { IColumnConfig } from '@/shared/view/ListLayout/List';
import { ListLayoutView } from '@/shared/view/ListLayout/ListLayout';
import { MobileListItem } from '@/shared/view/ListLayout/MobileListItem';

import { Feedback } from './components/Feedback';
import s from './styles.module.css';

type Props = {
  earnings: IEarning[];
  interval: { from?: Date; to?: Date };
  fetchEarnings: ({ periodName }: { periodName?: string | undefined }) => void;
  isLoading?: boolean;
};

const translatePeriodName = (intl: IntlShape, value: TPeriodName) => {
  switch (value) {
    case 'yesterday':
      return intl.formatMessage({ defaultMessage: 'Yesterday', id: '6dIxDP' });
    case 'last_month':
      return intl.formatMessage({ defaultMessage: 'Last month', id: 'y9ZbeM' });
    case 'month':
      return intl.formatMessage({ defaultMessage: 'Current month', id: 'AaULw5' });
    case 'current_week':
      return intl.formatMessage({ defaultMessage: 'Current week', id: 'xoUu9H' });
    case 'today':
      return intl.formatMessage({ defaultMessage: 'Today', id: 'zWgbGg' });
    case 'last_week':
      return intl.formatMessage({ defaultMessage: 'Last week', id: 'ynWMp2' });
  }
};

const getRowLink = ({ id, from, to }: { id: string; from?: Date; to?: Date }) =>
  `/journeys?for=${id}&from=${from ? formatDate(from) : ''}&to=${to ? formatDate(to) : ''}`;

const View = ({ earnings, interval, fetchEarnings, isLoading }: Props) => {
  const intl = useIntl();
  const history = useHistory();

  const currencyCode = useSelector(getCity)?.currencyCode;

  const columns: IColumnConfig<IEarning>[] = useMemo(
    () => [
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="chauffeur"
            id="fQ9BTa"
            description="overview table: column header"
          />
        ),
        renderCell: earning => earning.driverName,
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="rate"
            id="1Xw6Vi"
            description="overview table: column header"
          />
        ),
        renderCell: earning =>
          earning.acceptance !== null ? (
            <>
              <FormattedNumber value={earning.acceptance * 100} maximumFractionDigits={2} />%
            </>
          ) : null,
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="journeys"
            id="lTurO7"
            description="overview table: column header"
          />
        ),
        renderCell: earning => earning.count,
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="fare"
            id="VwiuM3"
            description="overview table: column header"
          />
        ),
        renderCell: earning => (
          <FormattedNumber value={earning.gross} style="currency" currency={currencyCode} />
        ),
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="commissions"
            id="wykj+i"
            description="overview table: column header"
          />
        ),
        renderCell: earning => (
          <FormattedNumber value={earning.commission} style="currency" currency={currencyCode} />
        ),
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="parking, toll road"
            id="01FUFE"
            description="overview table: column header"
          />
        ),
        renderCell: earning => (
          <FormattedNumber value={earning.parking} style="currency" currency={currencyCode} />
        ),
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="tips"
            id="VtD+PD"
            description="overview table: column header"
          />
        ),
        renderCell: earning => (
          <FormattedNumber value={earning.tips} style="currency" currency={currencyCode} />
        ),
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="other"
            id="81C4FK"
            description="overview table: column header"
          />
        ),
        renderCell: earning => (
          <FormattedNumber value={earning.other} style="currency" currency={currencyCode} />
        ),
      },
      {
        renderHeader: () => (
          <FormattedMessage
            defaultMessage="payouts"
            id="FeuG+/"
            description="overview table: column header"
          />
        ),
        renderCell: earning => (
          <FormattedNumber value={earning.payout} style="currency" currency={currencyCode} />
        ),
      },
    ],
    [currencyCode],
  );

  const onClickRow = useCallback(
    (earning: IEarning | undefined) =>
      earning && history.push(getRowLink({ id: earning.id, from: interval.from, to: interval.to })),
    [history, interval],
  );

  return (
    <div className={s.root}>
      <ListLayoutView<IEarning, 'periodName'>
        before={<Feedback />}
        title={
          <FormattedMessage
            defaultMessage="Overview"
            id="3H+PIM"
            description="Overview page title"
          />
        }
        data={earnings}
        after={<div className={s.bottomPadding} />}
        columns={columns}
        getEntityId={earning => String(earning.id)}
        onFetchData={fetchEarnings}
        shouldBeEmptyWithoutFilters={false}
        onClickRow={onClickRow}
        renderItem={earning => (
          <Link to={getRowLink({ id: earning.id, from: interval.from, to: interval.to })}>
            <MobileListItem
              title={earning.driverName}
              note={
                <FormattedMessage
                  defaultMessage="{journeys, plural, one {# journey} other {# journeys}}"
                  id="8WWIK+"
                  description="overview mobile list: count of journeys"
                  values={{ journeys: earning.count }}
                />
              }
              right={
                <FormattedNumber value={earning.payout} style="currency" currency={currencyCode} />
              }
            />
          </Link>
        )}
        filterConfig={[
          {
            name: 'periodName',
            type: 'select',
            initialValue: PeriodNameEnum.last_week,
            options: useMemo(
              () =>
                Object.keys(PeriodNameEnum).map(value => ({
                  value,
                  title: translatePeriodName(intl, value as TPeriodName),
                })),
              [intl],
            ),
          },
        ]}
        isLoading={isLoading}
      />
    </div>
  );
};

export { View };
